import { useState } from "react";
import { GlobalIcon, ModalDS } from "get-life-storybook-ts";
import { GuaranteeOption } from "../GuaranteeToggles/GuaranteeToggles";
import "./GuaranteePills.scss";

const INFO_TRAITS = new Set(["DISABILITY", "SERIOUS_ILLNESS"]);

interface GuaranteePillsProps {
  guarantees: GuaranteeOption[];
  onToggle: (key: string) => void;
  onCapitalChange?: (key: string, capital: number) => void;
  translate: (key: string) => string;
}

const STEP = 5000;

const formatCapital = (value: number): string =>
  new Intl.NumberFormat(
    process.env.REACT_APP_DOMAIN_LOCALE === "fr" ? "fr-FR" : "es-ES"
  ).format(value);

const sliderBackground = (
  value: number,
  min: number,
  max: number
): string => {
  const pct = max > min ? ((value - min) / (max - min)) * 100 : 0;
  return `linear-gradient(to right, #423EF4 0%, #423EF4 ${pct}%, #ECF0FF ${pct}%, #ECF0FF 100%)`;
};

const clampToStep = (value: number, min: number, max: number): number => {
  const clamped = Math.max(min, Math.min(max, value));
  return Math.round(clamped / STEP) * STEP;
};

const GuaranteePills = ({
  guarantees,
  onToggle,
  onCapitalChange,
  translate,
}: GuaranteePillsProps) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [pendingValue, setPendingValue] = useState<number | null>(null);
  const [inputDraft, setInputDraft] = useState("");
  const [infoTrait, setInfoTrait] = useState<GuaranteeOption | null>(null);

  if (guarantees.length === 0) return null;

  const startEdit = (g: GuaranteeOption) => {
    const initial = g.capital ?? 0;
    setEditingKey(g.key);
    setPendingValue(initial);
    setInputDraft(formatCapital(initial));
  };

  const commit = (g: GuaranteeOption, value: number) => {
    const min = g.minCapital ?? 0;
    const max = g.maxCapital ?? Number.MAX_SAFE_INTEGER;
    const stepped = clampToStep(value, min, max);
    setPendingValue(stepped);
    setInputDraft(formatCapital(stepped));
    if (onCapitalChange && stepped !== g.capital) {
      onCapitalChange(g.key, stepped);
    }
  };

  const finishEdit = (g: GuaranteeOption) => {
    if (pendingValue !== null) commit(g, pendingValue);
    setEditingKey(null);
    setPendingValue(null);
    setInputDraft("");
  };

  const onSliderChange = (raw: string) => {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    setPendingValue(parsed);
    setInputDraft(formatCapital(parsed));
  };

  const onInputBlur = (g: GuaranteeOption) => {
    const parsed = parseInt(inputDraft.replace(/\D/g, ""), 10);
    commit(g, Number.isNaN(parsed) ? g.capital ?? 0 : parsed);
  };

  return (
    <>
      <ul className="guarantee-pills">
        {guarantees.map((g) => {
        const isMandatory = !g.enabled;
        const canToggle = !isMandatory && !g.loading;
        const canEditCapital =
          !!onCapitalChange &&
          g.active &&
          g.capital !== undefined &&
          g.editableCapital !== false;
        const isEditing = editingKey === g.key;
        const sliderValue = isEditing && pendingValue !== null
          ? pendingValue
          : g.capital ?? 0;
        const min = g.minCapital ?? 0;
        const max = g.maxCapital ?? 0;
        const tooltip = INFO_TRAITS.has(g.key) ? (
          <button
            type="button"
            className="guarantee-pills__info"
            onClick={() => setInfoTrait(g)}
            aria-label={`Info ${g.label}`}
          >
            <GlobalIcon iconName="InfoIcon" size="XS" color="#8e8e8e" />
          </button>
        ) : null;

        return (
          <li
            key={g.key}
            className={`guarantee-pills__item${
              g.active ? " guarantee-pills__item--active" : ""
            }${g.loading ? " guarantee-pills__item--loading" : ""}${
              isEditing ? " guarantee-pills__item--editing" : ""
            }`}
          >
            <div className="guarantee-pills__row">
              <div className="guarantee-pills__body">
                {g.active ? (
                  <span className="guarantee-pills__title">
                    <GlobalIcon
                      iconName="CheckIcon"
                      size="XS"
                      color="#018565"
                    />
                    <strong>{g.label}</strong>
                    {tooltip}
                    {g.capital !== undefined ? (
                      isEditing ? (
                        <span className="guarantee-pills__capital guarantee-pills__capital--editing">
                          <input
                            type="text"
                            inputMode="numeric"
                            className="guarantee-pills__capitalInput"
                            value={inputDraft}
                            onChange={(e) => setInputDraft(e.target.value)}
                            onFocus={(e) => {
                              setInputDraft(
                                String(pendingValue ?? g.capital ?? 0)
                              );
                              e.target.select();
                            }}
                            onBlur={() => onInputBlur(g)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                (e.target as HTMLInputElement).blur();
                              } else if (e.key === "Escape") {
                                setInputDraft(formatCapital(sliderValue));
                                (e.target as HTMLInputElement).blur();
                              }
                            }}
                            disabled={g.loading}
                            aria-label={`${translate("upsell.guarantees.capital")} ${g.label}`}
                          />
                          <span> €</span>
                        </span>
                      ) : (
                        <span className="guarantee-pills__capital">
                          {formatCapital(sliderValue)} €
                        </span>
                      )
                    ) : (
                      <span className="guarantee-pills__status">
                        {isMandatory
                          ? translate("upsell.guarantees.mandatory")
                          : translate("upsell.guarantees.included")}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="guarantee-pills__title">
                    {translate("upsell.guarantees.add")}{" "}
                    <strong>{g.label}</strong>
                    {tooltip}
                  </span>
                )}
              </div>
              <div className="guarantee-pills__actions">
                {canEditCapital && (
                  <button
                    type="button"
                    className="guarantee-pills__edit"
                    onClick={() => (isEditing ? finishEdit(g) : startEdit(g))}
                    disabled={g.loading}
                    aria-expanded={isEditing}
                    aria-controls={`pill-editor-${g.key}`}
                  >
                    {isEditing
                      ? translate("newUpsell.v3.coverages.doneCapital")
                      : translate("newUpsell.v3.coverages.editCapital")}
                  </button>
                )}
                {canToggle && (
                  <button
                    type="button"
                    className={`guarantee-pills__action${
                      g.active ? " guarantee-pills__action--remove" : ""
                    }`}
                    onClick={() => onToggle(g.key)}
                    aria-label={`${
                      g.active
                        ? translate("upsell.guarantees.remove")
                        : translate("upsell.guarantees.add")
                    } ${g.label}`}
                  >
                    <GlobalIcon
                      iconName={g.active ? "MinusIcon" : "PlusIcon"}
                      size="XS"
                      color="white"
                    />
                  </button>
                )}
              </div>
            </div>
            {isEditing && canEditCapital && (
              <div
                id={`pill-editor-${g.key}`}
                className="guarantee-pills__editor"
              >
                <input
                  type="range"
                  className="guarantee-pills__slider"
                  min={min}
                  max={max}
                  step={STEP}
                  value={sliderValue}
                  onChange={(e) => onSliderChange(e.target.value)}
                  onPointerUp={() => commit(g, sliderValue)}
                  onKeyUp={(e) => {
                    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
                      commit(g, sliderValue);
                    }
                  }}
                  disabled={g.loading}
                  aria-label={`${translate("upsell.guarantees.capital")} ${g.label}`}
                  style={{ background: sliderBackground(sliderValue, min, max) }}
                />
                <div className="guarantee-pills__range">
                  <span>{formatCapital(min)} €</span>
                  <span>{formatCapital(max)} €</span>
                </div>
              </div>
            )}
          </li>
        );
      })}
      </ul>
      <ModalDS
        open={infoTrait !== null}
        onClose={() => setInfoTrait(null)}
        icon="InfoIcon"
        title={infoTrait?.label ?? ""}
        content={
          infoTrait
            ? translate(`newUpsell.v3.coverages.shortDesc.${infoTrait.key}`)
            : ""
        }
      />
    </>
  );
};

export default GuaranteePills;
